import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, EMPTY, Observable, Subject, defer, from, iif, of, throwError } from 'rxjs';
import { catchError, delay, filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { TrackingService, UserNotificationService } from '.';
import { repeat } from '../decorators/repeat';
import {
    DataState,
    DenyTrialExtensionPayload,
    ListResponse,
    ListSearch,
    LoadingState,
    RequestExtensionPayload,
    TrialExtensionRequest,
    UserLimit,
    UserUpdate,
} from '../models';
import { AdminUser } from '../models/admin-user.model';
import { ArivisViewer } from '../models/arivis-viewer.model';
import { MyPrivacySettings } from '../models/my-privacy-settings.model';
import { UserDetail } from '../models/user-detail.model';
import { UserInfo } from '../models/user-info.model';
import { UserSubscription } from '../models/user-subscription.model';
import { UserLimitKind } from '../types/user-limit-kind.type';
import { UserApiService } from './api/user-api/user.api.service';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private loginStateChange$$ = new Subject<boolean>();
    private _myInfo: UserInfo | undefined = undefined;
    private _isLoggedIn: boolean = false;

    private permissions$$: BehaviorSubject<string[] | null> = new BehaviorSubject(null);
    private permissionsError$$ = new BehaviorSubject(false);
    private profileImage$$: BehaviorSubject<string | undefined> = new BehaviorSubject(undefined);
    private userSubscriptionState$$: BehaviorSubject<DataState<UserSubscription>> = new BehaviorSubject({ loadingState: LoadingState.INIT });

    get permissions(): string[] | null {
        return this.permissions$$.getValue();
    }

    get permissions$(): Observable<string[] | null> {
        return this.permissions$$.asObservable();
    }

    get permissionsError$(): Observable<boolean> {
        return this.permissionsError$$.asObservable();
    }

    get myInfo(): UserInfo | undefined {
        return this._myInfo;
    }

    get isAdmin(): boolean {
        return !!this._myInfo?.isAdmin;
    }

    get userSub(): string | undefined | null {
        if (this.myInfo == null || !this.myInfo.sub) {
            return;
        }
        return this.myInfo.sub;
    }

    get isLoggedIn(): boolean {
        return this._isLoggedIn;
    }

    get profileImage$(): Observable<string | undefined> {
        return this.profileImage$$.asObservable();
    }

    get userSubscriptionState$(): Observable<DataState<UserSubscription>> {
        return this.userSubscriptionState$$.asObservable();
    }

    get userSubscription$(): Observable<UserSubscription> {
        return this.userApiService.getUserSubscription$();
    }

    constructor(
        private oauthService: OAuthService,
        private userApiService: UserApiService,
        private userNotificationService: UserNotificationService,
        private mixpanelTrackingService: TrackingService,
    ) {}

    refreshUserInfo$(): Observable<object | void> {
        this._myInfo = undefined;
        return of(this.oauthService.hasValidAccessToken()).pipe(
            delay(1000),
            switchMap(isValid => {
                return iif(
                    () => isValid,
                    defer(() =>
                        from(this.oauthService.loadUserProfile()).pipe(
                            tap((userInfo: object) => this.buildUserInfo(userInfo as { info: { [key: string]: string & number } })),
                            tap(() => this.refreshUserImages$()),
                            tap(() => this.checkLoginState(isValid)),
                            catchError((e: unknown) => throwError(e)),
                        ),
                    ),
                    of(this.checkLoginState(isValid)),
                );
            }),
        );
    }

    refreshUserImages$(): Observable<UserInfo> {
        if (this._myInfo?.sub) {
            return this.getMyProfileInfo$(this._myInfo.sub).pipe(
                tap((scopedUserInfo: UserInfo) => {
                    // Add date string in order to disable caching
                    scopedUserInfo.picture = scopedUserInfo.picture == null ? undefined : `${scopedUserInfo.picture}?${new Date().getTime()}`;

                    scopedUserInfo.headerImage = scopedUserInfo.headerImage == null ? undefined : `${scopedUserInfo.headerImage}?${new Date().getTime()}`;

                    if (this._myInfo != null) {
                        this._myInfo.headerImage = scopedUserInfo.headerImage;
                        this._myInfo.picture = scopedUserInfo.picture;
                    }

                    this.profileImage$$.next(this._myInfo?.picture);
                }),
            );
        }
        return throwError('Failed to refresh user images. No user sub was provided.');
    }

    logout(onlyCleanupStorage: boolean = false): void {
        // Get the userId before logging out for reseting Mixpanel
        const userId = this.myInfo?.sub || 'unknown_user';
        const eventKey = `session_started_${userId}`;

        this.oauthService.logOut(onlyCleanupStorage);
        localStorage.removeItem('simpleStorage');
        localStorage.removeItem('access_token');
        localStorage.removeItem('id_token');
        localStorage.removeItem(eventKey);

        // Reset Mixpanel tracking
        this.mixpanelTrackingService.resetTracking();

        // After logging out we need to set back user info
        this.refreshUserInfo$().subscribe();
    }

    getUserInfos$(ids: string[]): Observable<UserInfo[]> {
        return ids.length > 0 ? this.userApiService.getUserInfos$(ids) : of([]);
    }

    getMyProfileInfo$(id: string): Observable<UserInfo> {
        return this.userApiService.getMyProfileInfo$(id);
    }

    getMyPrivacySettings$(): Observable<MyPrivacySettings> {
        return this.userApiService.getMyPrivacySettings$();
    }

    loadUserSubscription(): void {
        if (this.oauthService.hasValidAccessToken()) {
            this.userSubscriptionState$$.next({ loadingState: LoadingState.LOADING });
            this.userApiService
                .getUserSubscription$()
                .pipe(
                    tap((userSubscription: UserSubscription) => {
                        this.userSubscriptionState$$.next({ loadingState: LoadingState.LOADED, data: userSubscription });
                    }),
                    take(1),
                )
                .subscribe();
        }
    }

    searchTrialExtensionRequests$(options: ListSearch): Observable<ListResponse<TrialExtensionRequest>> {
        return this.userApiService.searchTrialExtensionRequests$(options);
    }

    searchUsers$(options: ListSearch): Observable<ListResponse<AdminUser>> {
        return this.userApiService.searchUsers$(options);
    }

    deleteUsers$(userId: string): Observable<void> {
        return this.userApiService.deleteUser$(userId);
    }

    requestTrialExtension$(payload?: RequestExtensionPayload): Observable<void> {
        return this.userApiService.requestTrialExtension$(payload);
    }

    approveTrialExtension$(trialExtensionId: string): Observable<void> {
        return this.userApiService.approveTrialExtension$(trialExtensionId);
    }

    denyTrialExtension$(trialExtensionId: string, payload: DenyTrialExtensionPayload): Observable<void> {
        return this.userApiService.denyTrialExtension$(trialExtensionId, payload);
    }

    requestSubscriptionOffer$(): Observable<boolean> {
        return this.userApiService.requestSubscriptionOffer$();
    }

    cancelSubscription$(): Observable<boolean> {
        return this.userApiService.cancelSubscription$();
    }

    getUserLimits$(): Observable<Map<UserLimitKind, UserLimit | string>> {
        return this.userApiService.getUserLimits$();
    }

    isOwner(userId: string): boolean {
        return this.myInfo?.sub === userId;
    }

    hasPermissions(requiredPermissions: string[], respectAdminRole = true): boolean {
        // by default admin should have all permissions, but in some cases admin should not have specific permission
        if (this.isAdmin && respectAdminRole) {
            return true;
        }
        if (!requiredPermissions || requiredPermissions.length === 0) {
            return true;
        }
        return requiredPermissions.every(rp => (this.permissions$$.getValue() || []).includes(rp));
    }

    hasPermissions$(requiredPermissions: string[]): Observable<boolean> {
        if (this.isAdmin) {
            return of(true);
        }
        if (!requiredPermissions || requiredPermissions.length === 0) {
            return of(true);
        }
        return this.permissions$.pipe(
            filter(permissions => permissions != null),
            map(() => this.hasPermissions(requiredPermissions)),
        );
    }

    @repeat<string[]>(permissions => permissions.length > 0, 5)
    getPermissions$(): Observable<string[]> {
        this.userNotificationService.showSpinner();
        return this.userApiService.getPermissions$().pipe(
            tap(permissions => {
                this.permissions$$.next(permissions);
            }),
            catchError(() => {
                this.permissionsError$$.next(true);
                return EMPTY;
            }),
            finalize(() => {
                this.userNotificationService.hideSpinner();
            }),
        );
    }

    updateUserProfile$(payload: UserUpdate): Observable<UserInfo> {
        return this.userApiService.updateUserProfile$(payload);
    }

    searchUsersInfoByNameOrUserName$(query: string): Observable<UserDetail[]> {
        return this.userApiService.getUsersInfoByNameOrUserName$(query);
    }

    searchUsersInfo$(query: string): Observable<UserDetail[]> {
        return this.userApiService.getUserInfosByQuery$(query);
    }

    isCurrentUser(userId: string): boolean {
        return this.myInfo?.sub === userId;
    }

    isMine(userInfo: UserInfo | null, userInfoOfProfileToShow: UserInfo | null): boolean {
        let isMine = false;
        if (userInfo && userInfoOfProfileToShow) {
            isMine = userInfo.sub === userInfoOfProfileToShow.id;
        }
        return isMine;
    }

    getArivisViewer$(userId: string): Observable<ArivisViewer> {
        return this.userApiService.getArivisViewer$(userId);
    }

    protected createNewInstance(): UserInfo {
        return new UserInfo('', '', '');
    }

    private buildUserInfo(userInfo: { info: { [key: string]: string & number } }): void {
        this._myInfo = new UserInfo(
            userInfo?.info['user_id'],
            userInfo?.info['given_name'],
            userInfo?.info['family_name'],
            userInfo?.info['preferred_username'],
            userInfo?.info['locale'],
            userInfo?.info['sid'],
            userInfo?.info['nbf'],
            userInfo?.info['exp'],
            userInfo?.info['sub'],
            userInfo?.info['role'],
        );
        this._myInfo.email = userInfo?.info?.preferred_username;
    }

    private checkLoginState(isValid: boolean): void {
        const changed = this._isLoggedIn !== isValid;
        this._isLoggedIn = isValid;
        if (changed) {
            this.loginStateChange$$.next(this._isLoggedIn);
        }
    }
}
